/** Per-party identity badge for the Parties tab.
 *
 * The wording is the point. Tier 1 checks nothing against a company register,
 * so there is no "Verified" state to show — the clean case is "No issues",
 * which says what was actually established. A badge reading "Verified" would
 * tell the reviewer the opposite of the truth.
 *
 * Nothing here calls a company fake either. "Not found in the document" and
 * "cannot be valid" are provable; "fake" is not.
 */
import { AlertTriangle, CircleAlert, CircleHelp, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export interface PartyVerification {
  party_name: string;
  status: string;
  findings: string[];
}

const STYLES: Record<string, { label: string; className: string; Icon: typeof AlertTriangle }> = {
  placeholder: {
    label: 'Looks like placeholder data',
    className: 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/30 dark:text-red-300',
    Icon: AlertTriangle,
  },
  malformed: {
    label: 'Identifier cannot be valid',
    className:
      'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300',
    Icon: CircleAlert,
  },
  incomplete: {
    label: 'Nothing to check against',
    className: 'bg-muted text-muted-foreground border-border',
    Icon: CircleHelp,
  },
  unchecked: {
    label: 'No issues found',
    className:
      'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300',
    Icon: ShieldCheck,
  },
};

export function PartyVerificationBadge({ verification }: { verification?: PartyVerification }) {
  if (!verification) return null;
  const style = STYLES[verification.status] ?? STYLES.unchecked;
  const { Icon } = style;

  return (
    <div className="space-y-1">
      <Badge variant="outline" className={`text-[10px] gap-1 ${style.className}`}>
        <Icon className="h-3 w-3" aria-hidden="true" />
        {style.label}
      </Badge>
      {verification.findings.length > 0 && (
        <ul className="list-inside list-disc text-[11px] text-muted-foreground">
          {verification.findings.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
